/* eslint-disable prefer-const */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Image,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Picker } from '@react-native-picker/picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Mock Data
const mockInvoices = [
  {
    id: 'inv1',
    invoiceNumber: 'INV-2025-001',
    recipientName: 'Elite Construction Services',
    recipientType: 'Subcontractor',
    projectName: 'Commercial Office Building - London',
    issueDate: new Date('2025-05-01'),
    dueDate: new Date('2025-05-31'),
    amount: 12500.0,
    items: [
      { description: 'Foundation work', quantity: '1', unitPrice: '8000.00' },
      { description: 'Materials', quantity: '1', unitPrice: '4500.00' },
    ],
    status: 'Sent',
    notes: 'Initial invoice for phase 1 of the project.',
    attachments: [],
  },
  {
    id: 'inv2',
    invoiceNumber: 'INV-2025-002',
    recipientName: 'Premier Electrical Contractors',
    recipientType: 'Subcontractor',
    projectName: 'Residential Complex - Manchester',
    issueDate: new Date('2025-04-15'),
    dueDate: new Date('2025-05-15'),
    amount: 7800.0,
    items: [
      {
        description: 'Electrical installation',
        quantity: '1',
        unitPrice: '7800.00',
      },
    ],
    status: 'Partially Paid',
    notes: 'Payment due upon completion of electrical rough-in.',
    attachments: [],
  },
];

const mockProjects = [
  'Commercial Office Building - London',
  'Residential Complex - Manchester',
  'School Renovation - Birmingham',
  'Retail Fit-out - Leeds',
  'Healthcare Facility - Glasgow',
];

const mockRecipientTypes = ['Subcontractor', 'Supplier', 'Freelancer'];

const EditInvoiceScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();

  // State for company logo
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setLogoUrl(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setLogoUrl(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Defensive check for route.params
  if (!route.params) {
    console.warn('EditInvoiceScreen: route.params is undefined. Returning null or loading state.');
    return null;
  }

  let { invoiceId, allInvoices } = route.params;
  if (!Array.isArray(allInvoices)) {
    allInvoices = [];
  }

  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    recipientName: '',
    recipientType: Array.isArray(mockRecipientTypes) && mockRecipientTypes.length > 0 ? mockRecipientTypes[0] : '',
    projectName: Array.isArray(mockProjects) && mockProjects.length > 0 ? mockProjects[0] : '',
    issueDate: new Date(),
    dueDate: new Date(),
    amount: '',
    items: [],
    status: 'Draft',
    notes: '',
    attachments: [],
  });

  useEffect(() => {
    if (invoiceId && Array.isArray(allInvoices)) {
      const existingInvoice = allInvoices.find(inv => inv.id === invoiceId);
      if (existingInvoice) {
        setInvoiceData({
          ...existingInvoice,
          issueDate: new Date(existingInvoice.issueDate),
          dueDate: new Date(existingInvoice.dueDate),
          items: Array.isArray(existingInvoice.items) ? existingInvoice.items.map(item => ({ ...item })) : [],
          attachments: Array.isArray(existingInvoice.attachments) ? existingInvoice.attachments.map(item => ({ ...item })) : [],
        });
      } else {
        Alert.alert('Error', 'Invoice not found.');
        navigation.goBack();
      }
    }
  }, [invoiceId, allInvoices]);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerField, setDatePickerField] = useState(null);

  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) {
      setInvoiceData({
        ...invoiceData,
        [datePickerField]: selectedDate,
      });
    }
  };

  const showDatepicker = field => {
    setShowDatePicker(true);
    setDatePickerField(field);
  };

  const handleAddItem = () => {
    setInvoiceData(prevData => ({
      ...prevData,
      items: Array.isArray(prevData.items)
        ? [...prevData.items, { description: '', quantity: '', unitPrice: '' }]
        : [{ description: '', quantity: '', unitPrice: '' }],
    }));
  };

  const handleRemoveItem = index => {
    if (!Array.isArray(invoiceData.items)) return;
    const newItems = [...invoiceData.items];
    newItems.splice(index, 1);
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    if (!Array.isArray(invoiceData.items)) return;
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const calculateTotalAmount = () => {
    const items = Array.isArray(invoiceData.items) ? invoiceData.items : [];
    return items
      .reduce((total, item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        return total + quantity * unitPrice;
      }, 0)
      .toFixed(2);
  };

  const handleSubmit = () => {
    if (
      !invoiceData.invoiceNumber ||
      !invoiceData.recipientName ||
      !invoiceData.projectName ||
      (invoiceData.items?.length || 0) === 0
    ) {
      Alert.alert(
        'Error',
        'Please fill in all required fields and add at least one item.'
      );
      return;
    }

    Alert.alert('Success', 'Invoice updated successfully!');
    console.log('Updated Invoice Data:', invoiceData);
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollViewContent}>
        {/* Header with dynamic logo */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>

          {logoUrl ? (
            <Image source={{ uri: (logoUrl && logoUrl.startsWith('http')) ? logoUrl : undefined }} style={styles.headerLogo} />
          ) : (
            <Ionicons name="document-text-outline" size={30} color="#fff" />
          )}

          <Text style={styles.title}>Edit Invoice</Text>

          <View style={{ width: 30 }} />
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Invoice Details</Text>
          <TextInput
            style={styles.input}
            placeholder="Invoice Number"
            value={invoiceData.invoiceNumber}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, invoiceNumber: text })
            }
          />
          <TextInput
            style={styles.input}
            placeholder="Recipient Name"
            value={invoiceData.recipientName}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, recipientName: text })
            }
          />
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={invoiceData.recipientType}
              style={styles.picker}
              onValueChange={itemValue =>
                setInvoiceData({ ...invoiceData, recipientType: itemValue })
              }
            >
              {Array.isArray(mockRecipientTypes) &&
                mockRecipientTypes.map((type, index) => (
                  <Picker.Item key={index} label={type} value={type} />
                ))}
            </Picker>
          </View>
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={invoiceData.projectName}
              style={styles.picker}
              onValueChange={itemValue =>
                setInvoiceData({ ...invoiceData, projectName: itemValue })
              }
            >
              {Array.isArray(mockProjects) &&
                mockProjects.map((project, index) => (
                  <Picker.Item key={index} label={project} value={project} />
                ))}
            </Picker>
          </View>
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Dates</Text>
          <TouchableOpacity
            onPress={() => showDatepicker('issueDate')}
            style={styles.dateInput}
          >
            <Text>Issue Date: {invoiceData.issueDate.toLocaleDateString()}</Text>
            <Ionicons name="calendar-outline" size={20} color="#666" />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => showDatepicker('dueDate')}
            style={styles.dateInput}
          >
            <Text>Due Date: {invoiceData.dueDate.toLocaleDateString()}</Text>
            <Ionicons name="calendar-outline" size={20} color="#666" />
          </TouchableOpacity>
          {showDatePicker && (
            <DateTimePicker
              testID="dateTimePicker"
              value={
                datePickerField === 'issueDate'
                  ? invoiceData.issueDate
                  : invoiceData.dueDate
              }
              mode="date"
              display="default"
              onChange={handleDateChange}
            />
          )}
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Invoice Items</Text>
          {Array.isArray(invoiceData.items) &&
            invoiceData.items.map((item, index) => (
              <View key={index} style={styles.itemRow}>
                <TextInput
                  style={[styles.itemInput, { flex: 3 }]}
                  placeholder="Description"
                  value={item.description}
                  onChangeText={text =>
                    handleItemChange(index, 'description', text)
                  }
                />
                <TextInput
                  style={[styles.itemInput, { flex: 1 }]}
                  placeholder="Qty"
                  keyboardType="numeric"
                  value={item.quantity}
                  onChangeText={text =>
                    handleItemChange(index, 'quantity', text)
                  }
                />
                <TextInput
                  style={[styles.itemInput, { flex: 2 }]}
                  placeholder="Unit Price"
                  keyboardType="numeric"
                  value={item.unitPrice}
                  onChangeText={text =>
                    handleItemChange(index, 'unitPrice', text)
                  }
                />
                <TouchableOpacity
                  onPress={() => handleRemoveItem(index)}
                  style={styles.removeItemButton}
                >
                  <Ionicons name="remove-circle-outline" size={24} color="red" />
                </TouchableOpacity>
              </View>
            ))}
          <TouchableOpacity
            onPress={handleAddItem}
            style={styles.addItemButton}
          >
            <Ionicons name="add-circle-outline" size={24} color="#0e4e96" />
            <Text style={styles.addItemButtonText}>Add Item</Text>
          </TouchableOpacity>
          <Text style={styles.totalAmount}>
            Total Amount: £{calculateTotalAmount()}
          </Text>
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Additional Details</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Notes"
            multiline
            numberOfLines={4}
            value={invoiceData.notes}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, notes: text })
            }
          />
          <Text style={styles.attachmentText}>
            Attachments: {invoiceData.attachments?.length || 0} files
          </Text>
          <TouchableOpacity style={styles.attachButton}>
            <Ionicons name="attach-outline" size={20} color="#0e4e96" />
            <Text style={styles.attachButtonText}>Add Attachment</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity onPress={handleSubmit} style={styles.submitButton}>
          <Text style={styles.submitButtonText}>Save Changes</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  scrollViewContent: {
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    marginRight: 10,
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
    textAlign: 'center',
  },

  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  scrollViewContent: {
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButton: {
    marginRight: 10,
  },
  companyLogo: {
    width: 30,
    height: 30,
    marginRight: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  formSection: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#0e4e96',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    fontSize: 16,
  },
  dateInput: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    backgroundColor: '#fff',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginBottom: 10,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
    width: '100%',
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  itemInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 8,
    marginRight: 5,
    fontSize: 14,
  },
  removeItemButton: {
    marginLeft: 5,
  },
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0f7fa',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  addItemButtonText: {
    color: '#0e4e96',
    marginLeft: 5,
    fontWeight: 'bold',
  },
  totalAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'right',
    marginTop: 15,
    color: '#333',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  attachButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0f7fa',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  attachButtonText: {
    color: '#0e4e96',
    marginLeft: 5,
    fontWeight: 'bold',
  },
  attachmentText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  submitButton: {
    backgroundColor: '#fdb913',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 20,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default EditInvoiceScreen;
